import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useLocation, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../../components/ui/form";
import { Textarea } from "../../../../components/ui/textarea";
import StarIcon from "../../../../assets/svgs/common/star.svg";
import { usePostsRejectDraftAdminHttp } from "../../../../hooks/http/admin/posts/posts-reject-draft.admin.http";
import { usePostsRejectPostAdminHttp } from "../../../../hooks/http/admin/posts/posts-reject-post.admin.http";
import { useDisqualifyPostAdminHttp } from "../../../../hooks/http/admin/posts/posts-disqualify.admin.http";
import usePostStore from "../../../../store/post.store";
import { handleHttpError, useHandleNavigate } from "../../../../utils/utils";
import { useFlagPostAdminHttp } from "../../../../hooks/http/admin/posts/posts-flag.admin.http";
import { useToast } from "../../../../hooks/use-toast";
import { useSocialRejectAdminHttp } from "../../../../hooks/http/admin/socials/social-reject.admin.http";
import type React from "react";
import { useGetPostsByIdAdminHttp } from "../../../../hooks/http/admin/posts/get-post-by-id.admin.http";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";

const FormSchema = z.object({
  reasonKey: z.string().optional(),
  message: z.string().min(5, "Please provide a reason for rejection"),
});

// Predefined rejection reasons and templates by scenario
const DraftRejectReasons: Record<string, string> = {
  DOESNT_FOLLOW_BRIEF:
    "Your draft doesn't match the campaign brief. Please review the brief carefully and resubmit.",
  POOR_CONTENT_QUALITY:
    "This content doesn't meet our quality standards. Please improve (lighting, clarity, framing) and resubmit.",
  DUPLICATE_CONTENT:
    "This content appears to be copied or reused. Please submit original content for this campaign.",
  DUPLICATE_FROM_ANOTHER_PHASE:
    "This content was already submitted in a previous phase. Please create fresh content for this phase and resubmit.",
  WRONG_PRODUCT_OR_FORMAT:
    "The content doesn't feature the right product or format. Please refer to the brief and resubmit.",
};

const PostRejectReasons: Record<string, string> = {
  POST_LINK_UNAVAILABLE:
    "Your post link is unavailable or broken. Please check that the post is public and resubmit the correct link.",
  POST_WAS_DELETED:
    "Your post appears to have been deleted. Please repost the approved content and submit the new link.",
  WRONG_CONTENT_POSTED:
    "The content in this link doesn't match the approved draft. Please post the correct content and resubmit.",
  MISSING_REQUIRED_TAGS:
    "Your post is missing required hashtags or mentions as per the brief. Please update the post and resubmit the link.",
  MISSING_REQUIRED_CAPTION_TEXT:
    "Your caption is missing required text from the brief. Please update the caption and resubmit the link.",
};

const FlaggedReasons: Record<string, string> = {
  INSIGHTS_REQUIRED:
    "Please share a full screen recording of your post insights as soon as possible for verification.",
  INSIGHTS_RECORDING_INCOMPLETE:
    "The insights you shared are incomplete. Please share a full screen recording showing all metrics (views, reach, likes, comments) and resubmit.",
  INSIGHTS_NOT_MATCHING_POST:
    "The insights shared don't appear to match this post. Please share the correct insights screen recording for this specific post.",
};

const DisqualifiedReasons: Record<string, string> = {
  SUSPICIOUS_VIEWS:
    "We found seeded views and suspicious activity on your post. Your submission has been disqualified and no payout will be issued.",
  INSIGHTS_NOT_SUBMITTED:
    "Insights were not submitted within the required time limit. Your submission has been disqualified.",
  SAME_POST_MULTIPLE_CAMPAIGNS:
    "This post was submitted for multiple campaigns, which is not permitted. Your submission has been disqualified.",
  FAKE_OR_INFLATED_METRICS:
    "Your post shows signs of fake or inflated engagement metrics. Your submission has been disqualified and no payout will be issued.",
};

export const PostRejectModalPartial: React.FunctionComponent = () => {
  const { postId } = useParams();

  const { fetching: fetchingRejectdraft, execute: rejectDraft } =
    usePostsRejectDraftAdminHttp();
  const { fetching: fetchingRejectPost, execute: rejectPost } =
    usePostsRejectPostAdminHttp();

  const { fetching: fetchingDisquafliyPost, execute: disqualifyPost } =
    useDisqualifyPostAdminHttp();

  const { fetching: flaggingPost, execute: flagPost } = useFlagPostAdminHttp();

  const { fetching: fetchingRejectSocial, execute: rejectSocial } =
    useSocialRejectAdminHttp();

  const { execute: getPostById, fetching: fetchingPost } =
    useGetPostsByIdAdminHttp();

  const location = useLocation();
  const { removeById } = usePostStore();
  const { toast } = useToast();

  function handleReturnPath() {
    if (location.pathname.endsWith("draft-reject")) {
      return location.pathname.replace(`/${postId}/draft-reject`, "");
    }
    if (location.pathname.endsWith("post-reject")) {
      return location.pathname.replace(`/${postId}/post-reject`, "");
    }
    if (location.pathname.endsWith("disqualify")) {
      return location.pathname.replace(`/${postId}/disqualify`, "");
    }
    if (location.pathname.endsWith("flag")) {
      return location.pathname.replace(`/${postId}/flag`, "");
    }
    return "";
  }

  const { handleNavigate } = useHandleNavigate(handleReturnPath());

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      reasonKey: undefined,
      message: "",
    },
  });

  // Derive which reason set to use based on the current path
  const isDraftReject = location.pathname.endsWith("draft-reject");
  const isPostReject = location.pathname.endsWith("post-reject");
  const isDisqualify = location.pathname.endsWith("disqualify");
  const isFlag = location.pathname.endsWith("flag");

  const activeReasons = isDraftReject
    ? DraftRejectReasons
    : isPostReject
      ? PostRejectReasons
      : isFlag
        ? FlaggedReasons
        : isDisqualify
          ? DisqualifiedReasons
          : {};

  function handleReasonChange(reasonKey: string) {
    form.setValue("reasonKey", reasonKey, { shouldDirty: true });
    const template = activeReasons[reasonKey];
    if (template) {
      form.setValue("message", template, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!postId) return;

    if (location.pathname.endsWith("draft-reject")) {
      const response = await rejectDraft({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
    } else if (location.pathname.endsWith("post-reject")) {
      const response = await rejectPost({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
    } else if (location.pathname.endsWith("disqualify")) {
      const response = await disqualifyPost({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
    } else if (location.pathname.endsWith("flag")) {
      const response = await flagPost({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
    }
  }

  async function onRejectAndBan(data: z.infer<typeof FormSchema>) {
    if (!postId) return;

    // First, get the post details to extract the social/creator ID
    const postResponse = await getPostById({
      pathParams: { id: postId },
      body: null,
    });

    if (!postResponse.success) {
      return handleHttpError(postResponse, toast);
    }

    const socialId = postResponse.data.post.social?.id;

    if (!socialId) {
      toast({
        content: "Could not find creator information for this post",
        toastType: "error",
      });
      return;
    }

    // Reject the draft first
    const draftResponse = await rejectDraft({
      body: { message: data.message },
      pathParams: { id: postId },
    });

    if (!draftResponse.success) {
      return handleHttpError(draftResponse, toast);
    }
    // removeById(postId)

    // Then reject/ban the social
    const socialResponse = await rejectSocial({
      body: { message: data.message },
      pathParams: { id: socialId },
    });

    if (!socialResponse.success) {
      return handleHttpError(socialResponse, toast);
    }

    toast({
      content: "Draft rejected and creator banned successfully",
      toastType: "success",
    });

    removeById(postId);
    handleNavigate();
  }

  function ModalType() {
    if (location.pathname.endsWith("draft-reject")) {
      return {
        title: "Reason for rejecting draft",
        description: "Give a brief for the rejection of the draft",
        placeholder: "describe the reason for the draft rejection here...",
        buttonText: "Reject draft",
      };
    }
    if (location.pathname.endsWith("post-reject")) {
      return {
        title: "Reason for rejecting post",
        description: "Give a brief for the rejection of the post",
        placeholder: "describe the reason for the post rejection here...",
        buttonText: "Reject post",
      };
    }
    if (location.pathname.endsWith("disqualify")) {
      return {
        title: "Reason for disqualifying post",
        description: "Give a brief for the disqualification of the post",
        placeholder:
          "describe the reason for the post disqualification here...",
        buttonText: "Disqualify post",
      };
    }
    if (location.pathname.endsWith("flag")) {
      return {
        title: "Reason for flagging post",
        description: "Give a brief for the flagging of the post",
        placeholder: "describe the reason for the post flagging here...",
        buttonText: "Flag post",
      };
    }
  }

  function radioHeadingLabel() {
    if (location.pathname.endsWith("draft-reject")) {
      return "Choose a reason for rejecting draft";
    }
    if (location.pathname.endsWith("post-reject")) {
      return "Choose a reason for rejecting post";
    }
    if (location.pathname.endsWith("disqualify")) {
      return "Choose a reason for disqualifying post";
    }
    if (location.pathname.endsWith("flag")) {
      return "Choose a reason for flagging post";
    }
    return "Choose a reason";
  }

  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
      <DialogHeader className="w-full">
        <div className="flex flex-col">
          <p className="flex gap-1 font-semibold text-28 leading-8">
            {ModalType()?.title} <StarIcon />
          </p>
          <span className="text-secondary">{ModalType()?.description}</span>
        </div>
      </DialogHeader>
      <div className="border-border border-b w-full" />
      <div className="flex items-center gap-4 w-full">
        <Form {...form}>
          <form className="relative flex flex-col space-y-8 w-full">
            {/* Reason radios */}
            {Object.keys(activeReasons).length > 0 && (
              <div className="flex flex-col gap-4 text-left">
                <p className="flex items-center gap-1 font-semibold text-base">
                  {radioHeadingLabel()} <StarIcon />
                </p>
                <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
                  {Object.keys(activeReasons).map((key) => (
                    <div key={key} className="flex items-center gap-2">
                      <Input
                        type="radio"
                        id={key}
                        checked={form.watch("reasonKey") === key}
                        className="w-5 h-5 accent-primarybuttonbg"
                        value={key}
                        onChange={() => handleReasonChange(key)}
                      />
                      <Label
                        htmlFor={key}
                        className="font-medium text-sm leading-snug cursor-pointer"
                      >
                        {(() => {
                          switch (key) {
                            case "DOESNT_FOLLOW_BRIEF":
                              return "Doesn't follow the brief";
                            case "POOR_CONTENT_QUALITY":
                              return "Poor content quality";
                            case "DUPLICATE_CONTENT":
                              return "Copied / duplicate content";
                            case "DUPLICATE_FROM_ANOTHER_PHASE":
                              return "Duplicate from another phase";
                            case "WRONG_PRODUCT_OR_FORMAT":
                              return "Wrong product or format";
                            case "POST_LINK_UNAVAILABLE":
                              return "Post link unavailable";
                            case "POST_WAS_DELETED":
                              return "Post was deleted";
                            case "WRONG_CONTENT_POSTED":
                              return "Wrong content posted";
                            case "MISSING_REQUIRED_TAGS":
                              return "Missing required tags / mentions";
                            case "MISSING_REQUIRED_CAPTION_TEXT":
                              return "Missing required caption text";
                            case "INSIGHTS_REQUIRED":
                              return "Insights required";
                            case "INSIGHTS_RECORDING_INCOMPLETE":
                              return "Insights recording incomplete";
                            case "INSIGHTS_NOT_MATCHING_POST":
                              return "Insights not matching post";
                            case "SUSPICIOUS_VIEWS":
                              return "Suspicious / seeded views";
                            case "INSIGHTS_NOT_SUBMITTED":
                              return "Insights not submitted";
                            case "SAME_POST_MULTIPLE_CAMPAIGNS":
                              return "Same post for multiple campaigns";
                            case "FAKE_OR_INFLATED_METRICS":
                              return "Fake or inflated metrics";
                            default:
                              return key;
                          }
                        })()}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem className="text-start">
                  <FormControl>
                    <Textarea
                      className="px-6 pt-5 border border-border rounded-xl h-48 font-semibold placeholder:text-secondary resize-none"
                      placeholder={ModalType()?.placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center gap-5 self-end">
              {location.pathname.endsWith("draft-reject") && (
                <Button
                  type="button"
                  onClick={form.handleSubmit(onRejectAndBan)}
                  disabled={
                    fetchingRejectdraft ||
                    fetchingRejectSocial ||
                    fetchingPost ||
                    fetchingRejectPost ||
                    fetchingDisquafliyPost ||
                    flaggingPost
                  }
                  className="bg-rejectbuttonbg py-6 border border-rejectbuttonborder w-fit font-semibold text-rejectbuttontext tracking-wider"
                >
                  Reject and Ban
                </Button>
              )}

              <Button
                type="submit"
                onClick={form.handleSubmit(onSubmit)}
                disabled={
                  fetchingRejectdraft ||
                  fetchingRejectPost ||
                  fetchingDisquafliyPost ||
                  flaggingPost
                }
                className="bg-rejectbuttonbg py-6 border border-rejectbuttonborder w-fit font-semibold text-rejectbuttontext tracking-wider"
              >
                {ModalType()?.buttonText}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </DialogContent>
  );
};
